import { useEffect, useRef, useState } from 'react';
import { useOptionalFocusTimer } from '@/contexts/FocusTimerContext';
import { useNow } from '@/hooks/ui/useNow';
import { FocusPanel } from './FocusPanel';
import { FocusPill } from './FocusPill';
import { remainingMs } from '@/lib/focus/focusMachine';
import { toViewModel } from './focusViewModel';

/**
 * The Navbar's timer: the pill plus the panel it opens. Renders nothing when
 * no block is running, or outside a `FocusTimerProvider` (a bare Navbar).
 *
 * The click-outside and Escape handling is `LessonOptionsMenu`'s: a press on
 * the pill is left to the pill's own toggle, and Escape returns focus to it.
 */
export function FocusPillMenu({ className }: { className?: string }) {
  const timer = useOptionalFocusTimer();
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const phase = timer?.state.phase ?? 'idle';
  const now = useNow(phase !== 'idle' && phase !== 'paused');

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (pillRef.current?.contains(target)) return;
      if (!panelRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      pillRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!timer || phase === 'idle') return null;
  const { state, dispatch, openBreakSheet } = timer;
  const view = toViewModel(state, now);
  const left = remainingMs(state, now);
  const close = () => setOpen(false);

  return (
    <div className={className}>
      <div className='relative'>
        <FocusPill
          ref={pillRef}
          phase={phase}
          timeLabel={view.timeLabel}
          intention={view.intention}
          minutesLeft={left === null ? null : Math.ceil(left / 60_000)}
          aria-expanded={open}
          aria-haspopup='dialog'
          onClick={() => setOpen(o => !o)}
        />
        {open && (
          <FocusPanel
            ref={panelRef}
            view={view}
            className='absolute right-0 top-11 z-50'
            onPause={() => dispatch({ type: 'pause' })}
            onResume={() => dispatch({ type: 'resume' })}
            onFinish={() => dispatch({ type: 'finish' })}
            onNextBlock={() => dispatch({ type: 'nextBlock' })}
            onOpenBreak={() => {
              close();
              openBreakSheet();
            }}
            onAddNote={text => dispatch({ type: 'addNote', text })}
            onRemoveNote={id => dispatch({ type: 'removeNote', id })}
            onNavigate={close}
          />
        )}
      </div>
    </div>
  );
}
